import { AfterViewInit, ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { ActivatedRoute } from '@angular/router'

import { DateAdapter } from '@angular/material/core'
import { MatDatepicker } from '@angular/material/datepicker'
import { MatInput } from '@angular/material/input'

import { AsiakkaatDataSourceStateService, LukemanTyyppi } from './asiakkaat.datasource.service'

import { AsiakasService, LokalisoituAlvIlmoitusjakso, LokalisoituYritysmuoto } from '../../_angular/service/asiakas/asiakas.service'

import { AlvIlmoitusjakso, AsiakasListausTyolista, EiYhtiomuotoaYhtiomuoto } from '../../_jaettu-lemonator/model/asiakas'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'

import { Subject } from 'rxjs'
import { debounceTime, map, takeUntil } from 'rxjs/operators'

import { Platform } from '@angular/cdk/platform'
import { LocalMonth } from '@lemon/core/model/common'
import { LemonKuukausiDateAdapter } from 'app/_jaettu-angular/_material/LemonKuukausiDateAdapter'

import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { DateService } from '@lemon/core/service/date.service'
import { VersionTarkistusPalvelu } from 'app/_angular/service/version-tarkistus.palvelu'

export interface KuukausiJaValmiusaste extends LocalMonth {
  valmiusaste: number
  valmiusasteMuotoiltu: string
}

// interface TyontekoInfo {
//   asiakkaita: number,
//   asiakkaitaJoillaHinta: number,
//   nykyinenKuukausilaskutus: Big
//   keskimaarainenhinta: Big,
//   keskimaarainenPaivatavoite: Big
//   tehtyTanaan: Big
//   progressio: Big
//   seuraavaErapaiva: Date
//   paiviaSeuraavaanErapaivaan: number
//   progressioonLisattava: Big
//   dynaaminenSumma: Big
//   palluroidenSumma: Big
//   huutomerkkienSumma: Big
// }

interface AsiakkaidenHakuLomake {
  nimi: string
  yhtiomuoto: Yritysmuoto | EiYhtiomuotoaYhtiomuoto | null
  alvjakso: AlvIlmoitusjakso | null
  vuosikkalku: Date | null
  vuosikkloppu: Date | null
  minuutitvaitehot: LukemanTyyppi | null
  vaintoimiavaativat: boolean
}

@Component({
  templateUrl: './asiakkaat.component.html',
  styleUrls: ['./asiakkaat.component.css'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    { provide: DateAdapter, useClass: LemonKuukausiDateAdapter, deps: [ErrorHandler, DateService, Platform, LemonTranslationService] },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaatComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('hakuInput', { read: MatInput, static: true }) hakuInput: MatInput

  private ngUnsubscribe: Subject<void> = new Subject<void>()

  // tyontekoInfoObservable: Observable<TyontekoInfo>
  lomake: FieldTree<AsiakkaidenHakuLomake>

  yritysmuodot: LokalisoituYritysmuoto[]
  alvjaksot: LokalisoituAlvIlmoitusjakso[]
  minMonthStart = new Date(2015, 0, 1)
  readonly maxMonthStart = signal(new Date(2099, 11, 1))
  readonly minMonthEnd = signal(new Date(2015, 0, 1))
  maxMonthEnd = new Date(2099, 11, 1)

  // 'fivaldi' -listan asiakkaat hoidetaan AsiakkaatComponentOldissa
  lista: Exclude<AsiakasListausTyolista, 'fivaldi'> = 'tyolista'

  constructor(
    private _route: ActivatedRoute,
    private _asiakasDataSourceStateService: AsiakkaatDataSourceStateService,
    private _asiakasService: AsiakasService,
    private _dateService: DateService,
    private _versionTarkistusPalvelu: VersionTarkistusPalvelu,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    this.alvjaksot = this._asiakasService.alvIlmoitusjaksot
    this.yritysmuodot = this._asiakasService.yritysmuodotJaEiTiedossa

    this._route.url
      .pipe(
        map(urlSegments => {
          for (const o of urlSegments) {
            if (o.path === 'zen') {
              return true
            }
          }
          return false
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(showHolviAsiakkaat => {
        if (this._asiakasDataSourceStateService.naytaHolviPoolAsiakkaatSubject.value !== showHolviAsiakkaat) {
          // console.log('Set näytä holvi pool', showHolviAsiakkaat)
          this._asiakasDataSourceStateService.naytaHolviPoolAsiakkaatSubject.next(showHolviAsiakkaat)
        }
      })

    const aloitusaikavali = this._asiakasDataSourceStateService.getCurrentTimespan()
    const alkaa = this._dateService.localDateToDate({ year: aloitusaikavali.start.year, month: aloitusaikavali.start.month, day: 12 })
    this.minMonthEnd.set(alkaa)
    const loppuu = this._dateService.localDateToDate({ year: aloitusaikavali.end.year, month: aloitusaikavali.end.month, day: 12 })
    this.maxMonthStart.set(loppuu)

    this.lomake = form(
      signal<AsiakkaidenHakuLomake>({
        nimi: this._asiakasDataSourceStateService.vapaaTekstihakuObservable.value ?? '',
        yhtiomuoto: this._asiakasDataSourceStateService.yhtiomuotoObservable.value ?? null,
        alvjakso: this._asiakasDataSourceStateService.alvjaksoObservable.value ?? null,
        vuosikkalku: alkaa,
        vuosikkloppu: loppuu,
        minuutitvaitehot: this._asiakasDataSourceStateService.lukemanTyyppiObservable.value ?? null,
        vaintoimiavaativat: this._asiakasDataSourceStateService.naytaVainToimiaVaativatAsiakkaatObservable.value ?? null,
      }),
    )
    arvonMuutokset(this.lomake.nimi)
      .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value) {
          const val = value.trim().toLowerCase()
          this._asiakasDataSourceStateService.vapaaTekstihakuObservable.next(val)
        } else {
          this._asiakasDataSourceStateService.vapaaTekstihakuObservable.next(null)
        }
      })
    arvonMuutokset(this.lomake.yhtiomuoto)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        // console.log('Asetettu', value)
        this._asiakasDataSourceStateService.yhtiomuotoObservable.next(value)
      })
    arvonMuutokset(this.lomake.alvjakso)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        // console.log('Asetettu', value)
        this._asiakasDataSourceStateService.alvjaksoObservable.next(value)
      })
    arvonMuutokset(this.lomake.vuosikkalku)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (this.lomake.vuosikkalku().valid() && value) {
          // console.log('asetettu vuosikkalku', value)
          this.minMonthEnd.set(value)
          this._asiakasDataSourceStateService.setStartMonth({ month: value.getMonth() + 1, year: value.getFullYear() })
        }
      })
    arvonMuutokset(this.lomake.vuosikkloppu)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (this.lomake.vuosikkloppu().valid() && value) {
          // console.log('asetettu vuosikkloppu', value)
          this.maxMonthStart.set(value)
          this._asiakasDataSourceStateService.setEndMonth({ month: value.getMonth() + 1, year: value.getFullYear() })
        }
      })
    arvonMuutokset(this.lomake.minuutitvaitehot)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(arvo => {
        this._asiakasDataSourceStateService.lukemanTyyppiObservable.next(arvo)
      })
    arvonMuutokset(this.lomake.vaintoimiavaativat)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._asiakasDataSourceStateService.naytaVainToimiaVaativatAsiakkaatObservable.next(!!value)
      })

    this._asiakasDataSourceStateService.valittuAikavaliObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(aikavali => {
      if (aikavali) {
        if (aikavali.start) {
          const nykyinen = this.lomake.vuosikkalku().value()
          const month = (nykyinen?.getMonth() ?? -2) + 1
          const year = nykyinen?.getFullYear() ?? -1
          if (month !== aikavali.start.month || year !== aikavali.start.year) {
            const date = this._dateService.localDateToDate({ year: aikavali.start.year, month: aikavali.start.month, day: 12 })
            this.minMonthEnd.set(date)
            asetaArvo(this.lomake.vuosikkalku, date, { ilmoita: false })
          }
        }
        if (aikavali.end) {
          const nykyinen = this.lomake.vuosikkloppu().value()
          const month = (nykyinen?.getMonth() ?? -2) + 1
          const year = nykyinen?.getFullYear() ?? -1
          if (month !== aikavali.end.month || year !== aikavali.end.year) {
            const date = this._dateService.localDateToDate({ year: aikavali.end.year, month: aikavali.end.month, day: 12 })
            this.maxMonthStart.set(date)
            asetaArvo(this.lomake.vuosikkloppu, date, { ilmoita: false })
          }
        }
      }
    })

    this._asiakasService.asetaNykyinenAsiakas(null)
  }

  chosenMonthHandlerAlku(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.lomake.vuosikkalku, normalizedMonth)
    datepicker.close()
  }

  chosenMonthHandlerLoppu(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.lomake.vuosikkloppu, normalizedMonth)
    datepicker.close()
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this._asiakasService.asetaNykyinenAsiakas(null)
      this.hakuInput.focus()
      this._changeDetectorRef.markForCheck()
    }, 10)
  }

  ngOnInit() {
    const listaRouteData = this._route.snapshot.data?.lista
    if (listaRouteData === 'kasvu' || listaRouteData === 'tyolista') {
      this.lista = listaRouteData
    }

    this._versionTarkistusPalvelu.sovelluksenVersioObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(versio => {
      this._versionTarkistusPalvelu.tarkistaVersio(versio)
    })

    // this.tyontekoInfoObservable = this._asiakasDataSourceService.kaikkiKirjanpitajanAsiakkaat.pipe(
    //   map(asiakkaat => {

    //     const paluuarvo: TyontekoInfo = {
    //       asiakkaita: 0,
    //       asiakkaitaJoillaHinta: 0,
    //       keskimaarainenhinta: new Big(0),
    //       nykyinenKuukausilaskutus: new Big(0),
    //       keskimaarainenPaivatavoite: new Big(0),
    //       tehtyTanaan: new Big(0),
    //       progressio: new Big(0),
    //       seuraavaErapaiva: null,
    //       paiviaSeuraavaanErapaivaan: null,
    //       progressioonLisattava: new Big(0),
    //       dynaaminenSumma: new Big(0),
    //       palluroidenSumma: new Big(0),
    //       huutomerkkienSumma: new Big(0)
    //     }

    //     for (const asiakas of asiakkaat) {
    //       paluuarvo.asiakkaita++
    //       if (asiakas.voimassaolevaHinta && asiakas.voimassaolevaHinta.hinta) {
    //         paluuarvo.nykyinenKuukausilaskutus = paluuarvo.nykyinenKuukausilaskutus.plus(asiakas.voimassaolevaHinta.hinta)
    //         paluuarvo.asiakkaitaJoillaHinta++
    //       }
    //       if (asiakas.paivanSaldo) {
    //         paluuarvo.tehtyTanaan = paluuarvo.tehtyTanaan.plus(asiakas.paivanSaldo)
    //       }
    //       if (asiakas.palluroidenSumma) {
    //         paluuarvo.palluroidenSumma = paluuarvo.palluroidenSumma.plus(asiakas.palluroidenSumma)
    //         paluuarvo.progressio = paluuarvo.progressio.minus(asiakas.palluroidenSumma)
    //       }
    //       if (asiakas.huutomerkkienSumma) {
    //         paluuarvo.huutomerkkienSumma = paluuarvo.huutomerkkienSumma.plus(asiakas.huutomerkkienSumma)
    //         paluuarvo.progressio = paluuarvo.progressio.minus(asiakas.huutomerkkienSumma)
    //       }
    //       if (asiakas.dynaaminenHinta) {
    //         paluuarvo.dynaaminenSumma = paluuarvo.dynaaminenSumma.plus(asiakas.dynaaminenHinta)
    //       }
    //       if (asiakas.vahennysProgressiosta) {
    //         paluuarvo.progressioonLisattava = paluuarvo.progressioonLisattava.plus(asiakas.vahennysProgressiosta)
    //       }
    //     }
    //     if (paluuarvo.asiakkaitaJoillaHinta > 0) {
    //       paluuarvo.keskimaarainenhinta = paluuarvo.nykyinenKuukausilaskutus.div(paluuarvo.asiakkaitaJoillaHinta)
    //       paluuarvo.keskimaarainenPaivatavoite = paluuarvo.nykyinenKuukausilaskutus.div(20)
    //     }

    //     paluuarvo.dynaaminenSumma = new Big(0).minus(paluuarvo.keskimaarainenPaivatavoite).plus(paluuarvo.dynaaminenSumma)

    //     const nyt = this._dateService.dateToLocalDate(this._aikakone.annanNykyinenPaivamaara())

    //     paluuarvo.seuraavaErapaiva = this._dateService.localDateToDate(nyt.day > 12 ? this._dateService.lisaaKuukausiaPaikallinen(nyt, 1) : nyt)
    //     paluuarvo.seuraavaErapaiva.setDate(12)
    //     paluuarvo.paiviaSeuraavaanErapaivaan = this._dateService.laskeArkipaivatPaivienValilla(this._aikakone.annanNykyinenPaivamaara(), paluuarvo.seuraavaErapaiva)
    //     paluuarvo.progressio = paluuarvo.progressio.plus(paluuarvo.progressioonLisattava)

    //     // console.log('Lasketaan tyontekoinfoa.')

    //     return paluuarvo

    //   }),
    //   lemonShare()
    // )
  }
}
